import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Divider,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import {
  Disclose,
  ErrorState,
  Loading,
  Refusal,
  Stat,
  Stats,
} from "@lopie/kernel-ui";

/**
 * What Agent OS itself may touch, read from the kernel rather than stated.
 *
 * ## Why an app renders its own ceiling
 *
 * Because this one, of all of them, has to. Agent OS is a surface for
 * watching what agents are permitted to do; a version of it that asked you to
 * take its own permissions on trust would be making the opposite argument to
 * the one it ships. So the first thing it shows about itself is the row the
 * kernel holds — `is_self` out of `GET /apps`, computed by the kernel from
 * the manifest the token resolved, which is not a thing this page can assert.
 *
 * It is one row and not a register. Listing every app is the console's job
 * and doing it twice would be two screens that eventually disagree.
 *
 * ## The origin comes from the kernel too
 *
 * `ui_origin` is where this app type's UI is declared to be served, carried
 * on the manifest and composed by Terraform from the subdomain in
 * `manifests/agent-os.json`. It is deliberately *not* `window.location`: the
 * question this line answers is "where is this app declared to live", and
 * reading it off the address bar would answer "where am I", which is the same
 * string right up until somebody serves this bundle from somewhere it was
 * never declared to be — exactly the case worth seeing.
 *
 * An empty `ui_origin` is a fact rather than a gap. The kernel's bootstrap
 * client, the MCP server and every mounted remote have no origin of their
 * own, and a mounted remote's URL is its `mount_url` instead.
 */

function Section({ title, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export default function ThisApp({ get }) {
  const [me, setMe] = useState(null);
  const [failure, setFailure] = useState(null);

  const load = useCallback(async () => {
    setFailure(null);
    try {
      const body = await get("/apps");
      const mine = (body.apps || []).find((a) => a.is_self);
      // `null` and "not found" are different: a caller that reached `/apps`
      // and is not in its own answer is a state nothing should render as an
      // empty ceiling, which would read as "permitted nothing".
      setMe(mine || false);
    } catch (e) {
      setMe(null);
      setFailure(e);
    }
  }, [get]);

  useEffect(() => {
    load();
  }, [load]);

  if (failure) {
    return <Refusal failure={failure} actor="Agent OS" onRetry={load} />;
  }
  if (me === null) return <Loading label="Reading this app's ceiling" />;

  if (me === false) {
    return (
      <ErrorState
        failure={{
          status: 200,
          message:
            "The kernel answered with a list of apps and this app was not in "
            + "it. That is not a refusal and not an empty result — it means "
            + "the token resolved a manifest the kernel then did not return, "
            + "which is a kernel-side inconsistency rather than a permission.",
        }}
        title="This app is missing from its own answer"
        onRetry={load}
      />
    );
  }

  if (me.unparseable) {
    return (
      <Alert severity="error" variant="outlined">
        <AlertTitle>This app&apos;s manifest does not parse</AlertTitle>
        {me.detail}
      </Alert>
    );
  }

  const reads = me.reads || [];
  const serviceReads = me.service_reads || [];
  const actions = me.actions || [];

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        {/* An app client id is a long unbreakable monospace string and the
            chips beside it are not optional, so this row wraps rather than
            taking the page sideways at 390. */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", flexWrap: "wrap", mb: 1.5 }}
        >
          <Typography
            variant="h2"
            sx={{ fontFamily: "monospace", overflowWrap: "anywhere", minWidth: 0 }}
          >
            {me.app_type || me.app}
          </Typography>
          <Chip size="small" variant="outlined" label={`v${me.version}`} />
          <Chip size="small" variant="outlined" label={me.writes_scope || "global"} />
        </Stack>

        {/* The three numbers first, because "how much may this app do" is
            the question and three counts answer it before any list does. The
            lists stay underneath rather than going behind a control: they are
            short, and they are the point of this screen. */}
        <Stats sx={{ mb: 2.5 }}>
          <Stat
            value={actions.length}
            label={`action${actions.length === 1 ? "" : "s"} it may take`}
            title="Named verbs the kernel will accept from this app. An app that declares none cannot write at all — the verb is refused, not the record."
          />
          <Stat
            value={reads.length}
            label="types · with a person"
            title="What a signed-in person's token may read through this app. Still narrowed by that person's own groups on every request."
          />
          <Stat
            value={serviceReads.length}
            label="types · unattended"
            title="What this app may read with nobody behind the request. With no user term the intersection collapses to the manifest alone, which is why this is declared separately and reviewed separately."
          />
        </Stats>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 2, md: 4 }}
          sx={{ alignItems: "flex-start", mb: 2.5 }}
        >
          <Section title="Reads — with a user">
            <Typography variant="body2" color="text.secondary">
              {reads.length
                ? reads.map((g) => g.type).join(", ")
                : "Nothing. This app cannot read any business type on behalf of a signed-in person."}
            </Typography>
          </Section>
          <Section title="Reads — with no user behind it">
            <Typography variant="body2" color="text.secondary">
              {serviceReads.length
                ? serviceReads.map((g) => g.type).join(", ")
                : "Nothing. This app never runs unattended, so there is no declaration for it to run under."}
            </Typography>
          </Section>
        </Stack>

        <Section title="May do">
          {actions.length ? (
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
              {actions.map((a) => (
                <Chip
                  key={a}
                  size="small"
                  variant="outlined"
                  label={a}
                  sx={{ fontFamily: "monospace" }}
                />
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Nothing.
            </Typography>
          )}
        </Section>

        <Divider sx={{ my: 2 }} />

        {/* Identity and origin are the two facts here a person looks up
            rather than reads, so they are what moved behind a control — not
            the ceiling, which is what the screen is for. */}
        <Disclose
          summary={`Served from ${me.ui_origin || "no declared origin"}.`}
          label="Identity"
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 2, md: 4 }}
            sx={{ alignItems: "flex-start" }}
          >
            <Section title="Cognito app client">
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}
              >
                {me.app}
              </Typography>
            </Section>

            <Section title="Declared origin">
              {me.ui_origin ? (
                <Link
                  href={me.ui_origin}
                  variant="body2"
                  sx={{ overflowWrap: "anywhere" }}
                >
                  {me.ui_origin}
                </Link>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  none — this app declares no UI
                </Typography>
              )}
            </Section>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            The origin is where this app type is declared to be served,
            carried on the manifest — deliberately not{" "}
            <code>window.location</code>. Reading it off the address bar would
            answer &ldquo;where am I&rdquo;, which is the same string right up
            until somebody serves this bundle from somewhere it was never
            declared to be.
          </Typography>
        </Disclose>
      </Paper>
    </Stack>
  );
}
